import React, { useState, useRef } from 'react';
import {
  ChevronRight, X, Info,
  CheckCircle2, Upload, AlertCircle, DollarSign, Bitcoin, Coins,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { Currency, PaymentData, LoadedPaymentInfo } from './DatosDeCobroSection';
import {
  savePaymentInfo,
  PaymentInfoFieldError,
  FIXED_USDT_NETWORK,
} from '@/hooks/api/paymentInfo';

// ─── Constants ────────────────────────────────────────────────────────────────

const ACH_PLATFORMS = ['ARQ', 'Payoneer', 'Wise', 'Global66', 'Takenos', 'Revolut', 'Wallbit'] as const;
type AchPlatform = (typeof ACH_PLATFORMS)[number];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
  'Washington D.C.',
];

// ─── Validation helpers ───────────────────────────────────────────────────────

function validateABA(routing: string): boolean {
  if (!/^\d{9}$/.test(routing)) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  const sum = routing.split('').reduce((acc, d, i) => acc + Number(d) * w[i], 0);
  return sum % 10 === 0;
}

function validateAccountNumber(acc: string): boolean {
  return /^[a-zA-Z0-9]{4,17}$/.test(acc);
}

function maskAccount(acc: string): string {
  if (acc.length <= 4) return acc;
  return `${'*'.repeat(acc.length - 4)}${acc.slice(-4)}`;
}

// ─── CUIT helpers ─────────────────────────────────────────────────────────────

const CUIT_VALID_PREFIXES = ['20', '23', '24', '27', '30', '33', '34'];

function formatCuit(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

function cuitDigits(masked: string): string {
  return masked.replace(/\D/g, '');
}

function validateCUIT(digits: string): boolean {
  if (!/^\d{11}$/.test(digits)) return false;
  if (!CUIT_VALID_PREFIXES.includes(digits.slice(0, 2))) return false;
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = digits.slice(0, 10).split('').reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
  const remainder = sum % 11;
  if (remainder === 1) return false;
  const verifier = remainder === 0 ? 0 : 11 - remainder;
  return verifier === Number(digits[10]);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface AchFormValues {
  platform: AchPlatform | '';
  platformCustom: string;
  firstName: string;
  lastName: string;
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  accountType: 'checking' | 'savings';
  street: string;
  city: string;
  state: string;
  zip: string;
  screenshotFile: File | null;
}

type AchFormErrors = Partial<Record<keyof AchFormValues, string>>;

// ─── ARS types ────────────────────────────────────────────────────────────────

interface ArsFormValues {
  lbTag: string;
  cuit: string; // masked: XX-XXXXXXXX-X
}

type ArsFormErrors = Partial<Record<keyof ArsFormValues, string>>;

const INITIAL_ARS: ArsFormValues = { lbTag: '', cuit: '' };

// ─── USDT types ───────────────────────────────────────────────────────────────

const USDT_WALLETS = ['Binance', 'LB Finanzas', 'Bitso', 'Lemon', 'Belo', 'Buenbit', 'Ripio', 'MetaMask', 'Trust Wallet'] as const;
type UsdtWallet = (typeof USDT_WALLETS)[number];

interface UsdtFormValues {
  walletAddress: string;
  usdtPlatform: UsdtWallet | '';
  usdtPlatformCustom: string;
}

type UsdtFormErrors = Partial<Record<keyof UsdtFormValues, string>>;

const INITIAL_USDT: UsdtFormValues = { walletAddress: '', usdtPlatform: '', usdtPlatformCustom: '' };

// ─── USDT helpers ─────────────────────────────────────────────────────────────

function validateWalletAddress(addr: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(addr);
}

function maskWalletAddress(addr: string): string {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

const INITIAL_ACH: AchFormValues = {
  platform: '',
  platformCustom: '',
  firstName: '',
  lastName: '',
  bankName: '',
  routingNumber: '',
  accountNumber: '',
  accountType: 'checking',
  street: '',
  city: '',
  state: '',
  zip: '',
  screenshotFile: null,
};

// ─── Modality Selector ────────────────────────────────────────────────────────

interface ModalityCardProps {
  modality: Currency;
  label: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onSelect: () => void;
}

const ModalityCard: React.FC<ModalityCardProps> = ({ label, description, icon, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all w-full ${
      selected
        ? 'border-primary bg-primary/5'
        : 'border-gray-200 hover:border-primary/40 bg-white'
    }`}
  >
    <div className={`rounded-lg p-2 ${selected ? 'bg-primary/10' : 'bg-gray-100'}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-semibold ${selected ? 'text-primary' : 'text-gray-800'}`}>{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
    </div>
    {selected && <CheckCircle2 size={18} className="text-primary shrink-0" />}
    {!selected && <ChevronRight size={16} className="text-gray-300 shrink-0" />}
  </button>
);

// ─── Field components ─────────────────────────────────────────────────────────

function FieldWrapper({ label, error, required, children }: {
  label: string; error?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-600">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
}

function TextInput({
  value, onChange, onBlur, placeholder, error, type = 'text', inputMode, maxLength, 'data-testid': testId,
}: {
  value: string; onChange: (v: string) => void; onBlur: () => void;
  placeholder?: string; error?: boolean; type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number; 'data-testid'?: string;
}) {
  return (
    <input
      type={type}
      inputMode={inputMode}
      value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      maxLength={maxLength}
      data-testid={testId}
      className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white outline-none transition-colors
        focus:ring-2 focus:ring-primary/20
        ${error ? 'border-red-400 focus:ring-red-200' : 'border-border focus:border-primary'}`}
    />
  );
}

// ─── ACH Form ─────────────────────────────────────────────────────────────────

interface AchFormProps {
  values: AchFormValues;
  errors: AchFormErrors;
  onChange: (field: keyof AchFormValues, value: AchFormValues[keyof AchFormValues]) => void;
  onBlur: (field: keyof AchFormValues) => void;
  isCustomMode: boolean;
  onCustomModeChange: (v: boolean) => void;
}

const AchForm: React.FC<AchFormProps> = ({ values, errors, onChange, onBlur, isCustomMode, onCustomModeChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    onChange('screenshotFile', file);
    onBlur('screenshotFile');
  };

  const switchToCustom = () => {
    onChange('platform', '');
    onChange('platformCustom', '');
    onCustomModeChange(true);
  };

  const switchToDropdown = () => {
    onChange('platformCustom', '');
    onCustomModeChange(false);
    onBlur('platform');
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Platform */}
      <FieldWrapper label="Plataforma" error={errors.platform} required>
        {!isCustomMode ? (
          <>
            <Select
              value={values.platform}
              onValueChange={v => { onChange('platform', v as AchPlatform); onBlur('platform'); }}
            >
              <SelectTrigger
                data-testid="ach-platform-trigger"
                className={`h-10 text-sm border ${errors.platform ? 'border-red-400' : 'border-border'}`}
              >
                <SelectValue placeholder="Seleccioná tu plataforma" />
              </SelectTrigger>
              <SelectContent>
                {ACH_PLATFORMS.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* ARQ tip */}
            {values.platform === 'ARQ' && (
              <div
                data-testid="arq-tip"
                className="mt-1 flex gap-2 rounded-lg bg-blue-50 border border-blue-100 p-3"
              >
                <Info size={15} className="text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-snug">
                  En ARQ, encontrás tus datos ACH en <strong>Perfil → Cuenta bancaria → Datos ACH</strong>.
                  Usá el número de cuenta y routing que aparecen ahí.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={switchToCustom}
              className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 mt-0.5 w-fit"
              data-testid="platform-not-in-list-btn"
            >
              ¿No encontrás la tuya?
            </button>
          </>
        ) : (
          <>
            <TextInput
              value={values.platformCustom}
              onChange={v => { onChange('platformCustom', v); onBlur('platform'); }}
              onBlur={() => onBlur('platform')}
              placeholder="Nombre de tu plataforma"
              error={!!errors.platform}
              data-testid="ach-platform-custom"
            />
            <button
              type="button"
              onClick={switchToDropdown}
              className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 mt-0.5 w-fit"
              data-testid="platform-back-to-list-btn"
            >
              Elegir de la lista
            </button>
          </>
        )}
      </FieldWrapper>

      {/* Nombre / Apellido — separados a pedido de Fede (coordinado con psima:
          FIRST NAME / LAST NAME / ACCOUNT NAME combinado), reemplazan al viejo
          campo único "Nombre del beneficiario". */}
      <div className="grid grid-cols-2 gap-3">
        <FieldWrapper label="Nombre" error={errors.firstName} required>
          <TextInput
            value={values.firstName}
            onChange={v => onChange('firstName', v)}
            onBlur={() => onBlur('firstName')}
            placeholder="Ej: Juan"
            error={!!errors.firstName}
            data-testid="ach-first-name"
          />
        </FieldWrapper>

        <FieldWrapper label="Apellido" error={errors.lastName} required>
          <TextInput
            value={values.lastName}
            onChange={v => onChange('lastName', v)}
            onBlur={() => onBlur('lastName')}
            placeholder="Ej: Pérez"
            error={!!errors.lastName}
            data-testid="ach-last-name"
          />
        </FieldWrapper>
      </div>
      <p className="text-xs text-gray-400 leading-snug -mt-2">
        Deben coincidir exactamente con el nombre y apellido registrados en tu plataforma de cobro.
      </p>

      {/* Bank name / Routing number */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldWrapper label="Nombre del banco" error={errors.bankName} required>
          <TextInput
            value={values.bankName}
            onChange={v => onChange('bankName', v)}
            onBlur={() => onBlur('bankName')}
            placeholder="Ej: Lead Bank"
            error={!!errors.bankName}
            data-testid="ach-bank-name"
          />
        </FieldWrapper>

        <FieldWrapper label="ABA / Routing Number" error={errors.routingNumber} required>
          <TextInput
            value={values.routingNumber}
            onChange={v => onChange('routingNumber', v.replace(/\D/g, '').slice(0, 9))}
            onBlur={() => onBlur('routingNumber')}
            placeholder="9 dígitos · Ej: 101019644"
            inputMode="numeric"
            maxLength={9}
            error={!!errors.routingNumber}
            data-testid="ach-routing-number"
          />
        </FieldWrapper>
      </div>

      {/* Account number / Account type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldWrapper label="Número de cuenta" error={errors.accountNumber} required>
          <TextInput
            value={values.accountNumber}
            onChange={v => onChange('accountNumber', v.slice(0, 17))}
            onBlur={() => onBlur('accountNumber')}
            placeholder="Ej: 214738291065"
            error={!!errors.accountNumber}
            data-testid="ach-account-number"
          />
        </FieldWrapper>

        <FieldWrapper label="Tipo de cuenta" required>
          <div className="flex rounded-lg border border-border overflow-hidden w-fit" data-testid="ach-account-type-toggle">
            {(['checking', 'savings'] as const).map(type => (
              <button
                key={type}
                type="button"
                onClick={() => { onChange('accountType', type); onBlur('accountType'); }}
                data-testid={`ach-account-type-${type}`}
                className={`px-5 py-2 text-sm font-medium transition-colors ${
                  values.accountType === type
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {type === 'checking' ? 'Checking' : 'Savings'}
              </button>
            ))}
          </div>
        </FieldWrapper>
      </div>

      {/* Bank address */}
      <div className="flex flex-col gap-3">
        <p className="text-xs font-semibold text-gray-600">Dirección del banco <span className="text-red-500">*</span></p>

        <FieldWrapper label="Calle" error={errors.street}>
          <TextInput
            value={values.street}
            onChange={v => onChange('street', v)}
            onBlur={() => onBlur('street')}
            placeholder="Ej: 1801 Main St"
            error={!!errors.street}
            data-testid="ach-bank-street"
          />
        </FieldWrapper>

        <div className="grid grid-cols-2 gap-3">
          <FieldWrapper label="Ciudad" error={errors.city}>
            <TextInput
              value={values.city}
              onChange={v => onChange('city', v)}
              onBlur={() => onBlur('city')}
              placeholder="Ej: Kansas City"
              error={!!errors.city}
              data-testid="ach-bank-city"
            />
          </FieldWrapper>

          <FieldWrapper label="Estado" error={errors.state}>
            <Select
              value={values.state}
              onValueChange={v => { onChange('state', v); onBlur('state'); }}
            >
              <SelectTrigger
                data-testid="ach-bank-state-trigger"
                className={`h-10 text-sm border ${errors.state ? 'border-red-400' : 'border-border'}`}
              >
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldWrapper>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FieldWrapper label="Código postal" error={errors.zip}>
            <TextInput
              value={values.zip}
              onChange={v => onChange('zip', v.slice(0, 10))}
              onBlur={() => onBlur('zip')}
              placeholder="Ej: 64108"
              inputMode="numeric"
              error={!!errors.zip}
              data-testid="ach-bank-zip"
            />
          </FieldWrapper>

          <FieldWrapper label="País">
            <input
              type="text"
              value="United States"
              readOnly
              className="h-10 w-full rounded-md border border-border px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
            />
          </FieldWrapper>
        </div>
      </div>

      {/* Screenshot upload.
          TODO: `values.screenshotFile` NO se envía al POST — psima no persiste archivos
          en esta integración todavía. El input queda para que la psicóloga pueda revisar
          visualmente su captura antes de guardar, pero el submit no depende de este campo
          (no es requerido, ver ACH_REQUIRED_FIELDS). Si psima suma soporte de adjuntos,
          sumarlo al payload de USD en handleConfirmSave. */}
      <FieldWrapper label="Captura de pantalla" error={errors.screenshotFile}>
        <div
          className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors hover:border-primary/40 ${
            errors.screenshotFile ? 'border-red-300' : 'border-gray-200'
          }`}
          onClick={() => fileInputRef.current?.click()}
          data-testid="ach-screenshot-dropzone"
        >
          {values.screenshotFile ? (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
              <CheckCircle2 size={16} className="text-emerald-500" />
              <span className="font-medium truncate max-w-[200px]">{values.screenshotFile.name}</span>
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onChange('screenshotFile', null); }}
                className="text-gray-400 hover:text-red-500 transition-colors ml-1"
                aria-label="Eliminar archivo"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5 py-2">
              <Upload size={20} className="text-gray-400" />
              <p className="text-sm text-gray-600 font-medium">Subí una captura de pantalla</p>
              <p className="text-xs text-gray-400">JPG, PNG o PDF · Máximo 5 MB</p>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          className="hidden"
          onChange={handleFileChange}
          data-testid="ach-screenshot-input"
        />
      </FieldWrapper>
    </div>
  );
};

// ─── ARS Form ─────────────────────────────────────────────────────────────────

interface ArsFormProps {
  values: ArsFormValues;
  errors: ArsFormErrors;
  onChange: (field: keyof ArsFormValues, value: string) => void;
  onBlur: (field: keyof ArsFormValues) => void;
}

const ArsForm: React.FC<ArsFormProps> = ({ values, errors, onChange, onBlur }) => (
  <div className="flex flex-col gap-4" data-testid="ars-form">
    {/* Banner informativo */}
    <div
      data-testid="ars-lb-banner"
      className="flex gap-2 rounded-lg bg-blue-50 border border-blue-100 p-3"
    >
      <Info size={15} className="text-blue-500 shrink-0 mt-0.5" />
      <p className="text-xs text-blue-700 leading-snug">
        Los pagos en AR$ se procesan a través de <strong>LB Finanzas</strong>.
        El tipo de cambio aplicado es el de esa plataforma al día del pago.
      </p>
    </div>

    {/* LB Tag */}
    <FieldWrapper label="LB Tag" error={errors.lbTag} required>
      <div className={`flex h-10 w-full rounded-md border overflow-hidden transition-colors
        ${errors.lbTag ? 'border-red-400' : 'border-border focus-within:border-primary'}`}
      >
        <span
          data-testid="ars-lb-tag-prefix"
          className="flex items-center px-3 bg-gray-50 border-r border-border text-gray-500 text-sm font-medium select-none"
        >
          $
        </span>
        <input
          type="text"
          value={values.lbTag}
          onChange={e => onChange('lbTag', e.target.value.replace(/[$\s]/g, ''))}
          onBlur={() => onBlur('lbTag')}
          placeholder="juan.perez"
          data-testid="ars-lb-tag"
          className="flex-1 px-3 py-2 text-sm bg-white outline-none"
        />
      </div>
      <p className="text-xs text-gray-400 leading-snug mt-0.5">
        Ingresá tu LB Tag sin el símbolo $. Si tu tag es <strong>$juan.perez</strong>, escribí solo: <strong>juan.perez</strong>
      </p>
    </FieldWrapper>

    {/* CUIT */}
    <FieldWrapper label="CUIT" error={errors.cuit} required>
      <input
        type="text"
        inputMode="numeric"
        value={values.cuit}
        onChange={e => {
          const formatted = formatCuit(e.target.value);
          onChange('cuit', formatted);
        }}
        onBlur={() => onBlur('cuit')}
        placeholder="XX-XXXXXXXX-X"
        maxLength={13}
        data-testid="ars-cuit"
        className={`h-10 w-full rounded-md border px-3 py-2 text-sm bg-white outline-none transition-colors
          focus:ring-2 focus:ring-primary/20
          ${errors.cuit ? 'border-red-400 focus:ring-red-200' : 'border-border focus:border-primary'}`}
      />
    </FieldWrapper>
  </div>
);

// ─── ARS field validators ─────────────────────────────────────────────────────

function validateArsField(field: keyof ArsFormValues, values: ArsFormValues): string {
  switch (field) {
    case 'lbTag':
      if (!values.lbTag.trim()) return 'El LB Tag es obligatorio';
      if (/[$\s]/.test(values.lbTag)) return 'No debe contener $ ni espacios';
      return '';
    case 'cuit': {
      const digits = cuitDigits(values.cuit);
      if (!digits) return 'El CUIT es obligatorio';
      if (digits.length !== 11) return 'El CUIT debe tener 11 dígitos';
      if (!CUIT_VALID_PREFIXES.includes(digits.slice(0, 2))) return 'El prefijo del CUIT no es válido';
      if (!validateCUIT(digits)) return 'El dígito verificador del CUIT no es correcto';
      return '';
    }
    default:
      return '';
  }
}

const ARS_REQUIRED_FIELDS: (keyof ArsFormValues)[] = ['lbTag', 'cuit'];

// ─── USDT Form ────────────────────────────────────────────────────────────────

interface UsdtFormProps {
  values: UsdtFormValues;
  errors: UsdtFormErrors;
  onChange: (field: keyof UsdtFormValues, value: string) => void;
  onBlur: (field: keyof UsdtFormValues) => void;
  isCustomWalletMode: boolean;
  onCustomWalletModeChange: (v: boolean) => void;
}

const UsdtForm: React.FC<UsdtFormProps> = ({ values, errors, onChange, onBlur, isCustomWalletMode, onCustomWalletModeChange }) => (
  <div className="flex flex-col gap-4" data-testid="usdt-form">
    {/* Red fija */}
    <FieldWrapper label="Red">
      <input
        type="text"
        value="Polygon (MATIC)"
        disabled
        data-testid="usdt-network"
        className="h-10 w-full rounded-md border border-border px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
      />
      <p className="text-xs text-gray-400 leading-snug mt-0.5">
        Polygon (MATIC) es la única red habilitada para pagos en USDT.
      </p>
    </FieldWrapper>

    {/* Plataforma o wallet — requerido por psima (plataformaOWallet), aunque en el
        mock original era opcional. Ver PaymentDataFormModal error-mapping. */}
    <FieldWrapper label="Plataforma o wallet" error={errors.usdtPlatform} required>
      {!isCustomWalletMode ? (
        <>
          <Select
            value={values.usdtPlatform}
            onValueChange={v => { onChange('usdtPlatform', v); onBlur('usdtPlatform'); }}
          >
            <SelectTrigger
              data-testid="usdt-platform-trigger"
              className={`h-10 text-sm border ${errors.usdtPlatform ? 'border-red-400' : 'border-border'}`}
            >
              <SelectValue placeholder="Seleccioná tu plataforma o wallet" />
            </SelectTrigger>
            <SelectContent>
              {USDT_WALLETS.map(w => (
                <SelectItem key={w} value={w}>{w}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-400 leading-snug mt-0.5">
            Indicarlo nos ayuda a brindarte soporte más rápido si surge alguna duda.
          </p>
          <button
            type="button"
            onClick={() => { onChange('usdtPlatform', ''); onChange('usdtPlatformCustom', ''); onCustomWalletModeChange(true); }}
            className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 mt-0.5 w-fit"
            data-testid="usdt-platform-not-in-list-btn"
          >
            ¿No encontrás la tuya?
          </button>
        </>
      ) : (
        <>
          <TextInput
            value={values.usdtPlatformCustom}
            onChange={v => { onChange('usdtPlatformCustom', v); onBlur('usdtPlatform'); }}
            onBlur={() => onBlur('usdtPlatform')}
            placeholder="Nombre de tu plataforma o wallet"
            error={!!errors.usdtPlatform}
            data-testid="usdt-platform-custom"
          />
          <p className="text-xs text-gray-400 leading-snug mt-0.5">
            Indicarlo nos ayuda a brindarte soporte más rápido si surge alguna duda.
          </p>
          <button
            type="button"
            onClick={() => { onChange('usdtPlatformCustom', ''); onCustomWalletModeChange(false); onBlur('usdtPlatform'); }}
            className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 mt-0.5 w-fit"
            data-testid="usdt-platform-back-to-list-btn"
          >
            Elegir de la lista
          </button>
        </>
      )}
    </FieldWrapper>

    {/* Wallet address */}
    <FieldWrapper label="Dirección de wallet" error={errors.walletAddress} required>
      <TextInput
        value={values.walletAddress}
        onChange={v => onChange('walletAddress', v.trim())}
        onBlur={() => onBlur('walletAddress')}
        placeholder="0x..."
        error={!!errors.walletAddress}
        maxLength={42}
        data-testid="usdt-wallet-address"
      />
    </FieldWrapper>

    {/* Irreversibility warning */}
    <div
      data-testid="usdt-irreversibility-banner"
      className="flex gap-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3"
    >
      <AlertCircle size={15} className="text-yellow-600 shrink-0 mt-0.5" />
      <p className="text-xs text-yellow-800 leading-snug">
        <strong>Los pagos en USDT son irreversibles.</strong> Verificá que la dirección de wallet sea correcta antes de confirmar. No podemos recuperar fondos enviados a una dirección incorrecta.
      </p>
    </div>
  </div>
);

function validateUsdtField(field: keyof UsdtFormValues, values: UsdtFormValues): string {
  if (field === 'walletAddress') {
    if (!values.walletAddress.trim()) return 'La dirección de wallet es obligatoria';
    if (!validateWalletAddress(values.walletAddress)) return 'Debe empezar con 0x y tener exactamente 42 caracteres hexadecimales';
    return '';
  }
  if (field === 'usdtPlatform') {
    return (values.usdtPlatform || values.usdtPlatformCustom.trim())
      ? ''
      : 'Seleccioná una plataforma o wallet, o escribí la tuya';
  }
  return '';
}

const USDT_REQUIRED_FIELDS: (keyof UsdtFormValues)[] = ['walletAddress', 'usdtPlatform'];

// ─── Confirmation Modal ───────────────────────────────────────────────────────

interface ConfirmModalProps {
  open: boolean;
  rows: Array<{ label: string; value: string; testId?: string }>;
  saving: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ open, rows, saving, onConfirm, onBack }) => (
  <Dialog open={open} onOpenChange={v => { if (!v && !saving) onBack(); }}>
    <DialogContent className="sm:max-w-md">
      <div className="px-6 pb-6 pt-4 flex flex-col gap-5">
        <DialogHeader>
          <DialogTitle>Confirmá tus datos de cobro</DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            Revisá que los datos sean correctos antes de guardar.
          </p>
        </DialogHeader>

        <div className="flex flex-col gap-3 rounded-xl bg-gray-50 border border-gray-100 p-4 text-sm">
          {rows.map(row => (
            <SummaryRow key={row.label} label={row.label} value={row.value} testId={row.testId} />
          ))}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack} disabled={saving} data-testid="confirm-back-btn">
            Volver
          </Button>
          <Button variant="default" className="flex-1" onClick={onConfirm} disabled={saving} data-testid="confirm-save-btn">
            {saving ? 'Guardando...' : 'Guardar datos'}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

function SubmitErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      data-testid="payment-info-submit-error"
      className="flex gap-2 rounded-lg bg-red-50 border border-red-200 p-3"
    >
      <AlertCircle size={15} className="text-red-500 shrink-0 mt-0.5" />
      <p className="text-xs text-red-700 leading-snug">{error}</p>
    </div>
  );
}

function SummaryRow({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="font-medium text-gray-800 text-right" data-testid={testId}>{value}</span>
    </div>
  );
}

// ─── ACH field validators ─────────────────────────────────────────────────────

function validateAchField(field: keyof AchFormValues, values: AchFormValues): string {
  switch (field) {
    case 'platform':
      return (values.platform || values.platformCustom.trim()) ? '' : 'Seleccioná una plataforma o escribí la tuya';
    case 'firstName':
      return values.firstName.trim() ? '' : 'El nombre es obligatorio';
    case 'lastName':
      return values.lastName.trim() ? '' : 'El apellido es obligatorio';
    case 'bankName':
      return values.bankName.trim() ? '' : 'El nombre del banco es obligatorio';
    case 'routingNumber':
      if (!values.routingNumber) return 'El routing number es obligatorio';
      if (!/^\d{9}$/.test(values.routingNumber)) return 'Debe tener exactamente 9 dígitos';
      if (!validateABA(values.routingNumber)) return 'El routing number no es válido (checksum incorrecto)';
      return '';
    case 'accountNumber':
      if (!values.accountNumber) return 'El número de cuenta es obligatorio';
      if (!validateAccountNumber(values.accountNumber)) return 'De 4 a 17 caracteres alfanuméricos';
      return '';
    case 'street':
      return values.street.trim() ? '' : 'La calle es obligatoria';
    case 'city':
      return values.city.trim() ? '' : 'La ciudad es obligatoria';
    case 'state':
      return values.state ? '' : 'El estado es obligatorio';
    case 'zip':
      return values.zip.trim() ? '' : 'El código postal es obligatorio';
    case 'screenshotFile': {
      if (!values.screenshotFile) return '';
      const allowed = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowed.includes(values.screenshotFile.type)) return 'Solo JPG, PNG o PDF';
      if (values.screenshotFile.size > 5 * 1024 * 1024) return 'El archivo supera los 5 MB';
      return '';
    }
    default:
      return '';
  }
}

const ACH_REQUIRED_FIELDS: (keyof AchFormValues)[] = [
  'platform', 'firstName', 'lastName', 'bankName', 'routingNumber',
  'accountNumber', 'street', 'city', 'state', 'zip',
];

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface PaymentDataFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (info: LoadedPaymentInfo) => void;
  initialData?: LoadedPaymentInfo | null;
}

function achFromData(d: Extract<PaymentData, { currency: 'USD' }>): AchFormValues {
  const isKnown = ACH_PLATFORMS.includes(d.plataforma as AchPlatform);
  return {
    platform: isKnown ? (d.plataforma as AchPlatform) : '',
    platformCustom: isKnown ? '' : d.plataforma,
    firstName: d.nombre,
    lastName: d.apellido,
    bankName: d.nombreBanco,
    routingNumber: d.routingNumber,
    accountNumber: d.numeroCuenta,
    accountType: d.tipoCuenta,
    street: d.direccionBanco.calle,
    city: d.direccionBanco.ciudad,
    state: d.direccionBanco.estado,
    zip: d.direccionBanco.codigoPostal,
    screenshotFile: null,
  };
}

function arsFromData(d: Extract<PaymentData, { currency: 'ARS' }>): ArsFormValues {
  return {
    lbTag: d.lbTag,
    cuit: d.cuit.length === 11 ? formatCuit(d.cuit) : d.cuit,
  };
}

function usdtFromData(d: Extract<PaymentData, { currency: 'USDT' }>): UsdtFormValues {
  const isKnown = !!d.plataformaOWallet && USDT_WALLETS.includes(d.plataformaOWallet as UsdtWallet);
  return {
    walletAddress: d.direccionWallet,
    usdtPlatform: isKnown ? (d.plataformaOWallet as UsdtWallet) : '',
    usdtPlatformCustom: isKnown ? '' : (d.plataformaOWallet ?? ''),
  };
}

/** Mapeo de campos que devuelve psima en un 400 → campo del form correspondiente. */
const ACH_FIELD_MAP: Record<string, keyof AchFormValues> = {
  plataforma: 'platform',
  nombre: 'firstName',
  apellido: 'lastName',
  nombreBanco: 'bankName',
  routingNumber: 'routingNumber',
  numeroCuenta: 'accountNumber',
  tipoCuenta: 'accountType',
  'direccionBanco.calle': 'street',
  'direccionBanco.ciudad': 'city',
  'direccionBanco.estado': 'state',
  'direccionBanco.codigoPostal': 'zip',
};
const ARS_FIELD_MAP: Record<string, keyof ArsFormValues> = {
  lbTag: 'lbTag',
  cuit: 'cuit',
};
const USDT_FIELD_MAP: Record<string, keyof UsdtFormValues> = {
  direccionWallet: 'walletAddress',
  plataformaOWallet: 'usdtPlatform',
};

export const PaymentDataFormModal: React.FC<PaymentDataFormModalProps> = ({
  open, onClose, onSaved, initialData,
}) => {
  const isEditing = !!initialData;
  const initialCurrency = initialData?.data.currency ?? null;

  const [selectedModality, setSelectedModality] = useState<Currency | null>(initialCurrency);
  // Pending modality when a change-warning confirmation is needed
  const [pendingModality, setPendingModality] = useState<Currency | null>(null);
  const [achValues, setAchValues] = useState<AchFormValues>(
    initialData && initialData.data.currency === 'USD' ? achFromData(initialData.data) : INITIAL_ACH,
  );
  const [achErrors, setAchErrors] = useState<AchFormErrors>({});
  const [arsValues, setArsValues] = useState<ArsFormValues>(
    initialData && initialData.data.currency === 'ARS' ? arsFromData(initialData.data) : INITIAL_ARS,
  );
  const [arsErrors, setArsErrors] = useState<ArsFormErrors>({});
  const [usdtValues, setUsdtValues] = useState<UsdtFormValues>(
    initialData && initialData.data.currency === 'USDT' ? usdtFromData(initialData.data) : INITIAL_USDT,
  );
  const [usdtErrors, setUsdtErrors] = useState<UsdtFormErrors>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCustomPlatformMode, setIsCustomPlatformMode] = useState(
    initialData && initialData.data.currency === 'USD'
      ? !ACH_PLATFORMS.includes(initialData.data.plataforma as AchPlatform)
      : false,
  );
  const [isCustomWalletMode, setIsCustomWalletMode] = useState(
    initialData && initialData.data.currency === 'USDT'
      ? !!initialData.data.plataformaOWallet && !USDT_WALLETS.includes(initialData.data.plataformaOWallet as UsdtWallet)
      : false,
  );

  // Re-sync when initialData changes (e.g. modal re-opened with different data)
  const prevOpen = React.useRef(false);
  React.useEffect(() => {
    if (open && !prevOpen.current) {
      setSelectedModality(initialCurrency);
      setAchValues(initialData && initialData.data.currency === 'USD' ? achFromData(initialData.data) : INITIAL_ACH);
      setAchErrors({});
      setArsValues(initialData && initialData.data.currency === 'ARS' ? arsFromData(initialData.data) : INITIAL_ARS);
      setArsErrors({});
      setUsdtValues(initialData && initialData.data.currency === 'USDT' ? usdtFromData(initialData.data) : INITIAL_USDT);
      setUsdtErrors({});
      setShowConfirm(false);
      setSubmitError(null);
      setIsCustomPlatformMode(
        initialData && initialData.data.currency === 'USD'
          ? !ACH_PLATFORMS.includes(initialData.data.plataforma as AchPlatform)
          : false,
      );
      setIsCustomWalletMode(
        initialData && initialData.data.currency === 'USDT'
          ? !!initialData.data.plataformaOWallet && !USDT_WALLETS.includes(initialData.data.plataformaOWallet as UsdtWallet)
          : false,
      );
      setPendingModality(null);
    }
    prevOpen.current = open;
  }, [open, initialData, initialCurrency]);

  const handleClose = () => {
    setSelectedModality(null);
    setAchValues(INITIAL_ACH);
    setAchErrors({});
    setArsValues(INITIAL_ARS);
    setArsErrors({});
    setUsdtValues(INITIAL_USDT);
    setUsdtErrors({});
    setShowConfirm(false);
    setSubmitError(null);
    setIsCustomPlatformMode(false);
    setIsCustomWalletMode(false);
    setPendingModality(null);
    onClose();
  };

  /** Vuelca los errores de campo de un 400 de psima al form correspondiente. Las
   * claves que no matchean ningún campo conocido (ej. `_general`) se muestran
   * como banner genérico en vez de perderse. */
  const applyFieldErrors = (errors: Record<string, string>) => {
    const achOut: AchFormErrors = {};
    const arsOut: ArsFormErrors = {};
    const usdtOut: UsdtFormErrors = {};
    const unmapped: string[] = [];

    for (const [key, message] of Object.entries(errors)) {
      if (key === '_general') { unmapped.push(message); continue; }
      if (ACH_FIELD_MAP[key]) achOut[ACH_FIELD_MAP[key]] = message;
      else if (ARS_FIELD_MAP[key]) arsOut[ARS_FIELD_MAP[key]] = message;
      else if (USDT_FIELD_MAP[key]) usdtOut[USDT_FIELD_MAP[key]] = message;
      else unmapped.push(message);
    }

    if (Object.keys(achOut).length) setAchErrors(prev => ({ ...prev, ...achOut }));
    if (Object.keys(arsOut).length) setArsErrors(prev => ({ ...prev, ...arsOut }));
    if (Object.keys(usdtOut).length) setUsdtErrors(prev => ({ ...prev, ...usdtOut }));
    if (unmapped.length) setSubmitError(unmapped.join(' '));
  };

  // ACH handlers
  const handleAchChange = (field: keyof AchFormValues, value: AchFormValues[keyof AchFormValues]) => {
    setAchValues(prev => ({ ...prev, [field]: value }));
    if (achErrors[field]) setAchErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleAchBlur = (field: keyof AchFormValues) => {
    setAchErrors(prev => ({ ...prev, [field]: validateAchField(field, achValues) }));
  };

  const handleAchSubmit = () => {
    setSubmitError(null);
    const newErrors: AchFormErrors = {};
    let hasError = false;
    for (const field of ACH_REQUIRED_FIELDS) {
      const error = validateAchField(field, achValues);
      if (error) { newErrors[field] = error; hasError = true; }
    }
    const fileError = validateAchField('screenshotFile', achValues);
    if (fileError) { newErrors.screenshotFile = fileError; hasError = true; }
    setAchErrors(newErrors);
    if (!hasError) setShowConfirm(true);
  };

  // ARS handlers
  const handleArsChange = (field: keyof ArsFormValues, value: string) => {
    setArsValues(prev => ({ ...prev, [field]: value }));
    if (arsErrors[field]) setArsErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleArsBlur = (field: keyof ArsFormValues) => {
    setArsErrors(prev => ({ ...prev, [field]: validateArsField(field, arsValues) }));
  };

  const handleArsSubmit = () => {
    setSubmitError(null);
    const newErrors: ArsFormErrors = {};
    let hasError = false;
    for (const field of ARS_REQUIRED_FIELDS) {
      const error = validateArsField(field, arsValues);
      if (error) { newErrors[field] = error; hasError = true; }
    }
    setArsErrors(newErrors);
    if (!hasError) setShowConfirm(true);
  };

  // USDT handlers
  const handleUsdtChange = (field: keyof UsdtFormValues, value: string) => {
    setUsdtValues(prev => ({ ...prev, [field]: value }));
    if (usdtErrors[field]) setUsdtErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleUsdtBlur = (field: keyof UsdtFormValues) => {
    setUsdtErrors(prev => ({ ...prev, [field]: validateUsdtField(field, usdtValues) }));
  };

  const handleUsdtSubmit = () => {
    setSubmitError(null);
    const newErrors: UsdtFormErrors = {};
    let hasError = false;
    for (const field of USDT_REQUIRED_FIELDS) {
      const error = validateUsdtField(field, usdtValues);
      if (error) { newErrors[field] = error; hasError = true; }
    }
    setUsdtErrors(newErrors);
    if (!hasError) setShowConfirm(true);
  };

  // Confirm rows per modality
  const confirmRows: Array<{ label: string; value: string; testId?: string }> =
    selectedModality === 'USD'
      ? [
          { label: 'Modalidad', value: 'USD vía ACH' },
          ...((achValues.platform || achValues.platformCustom.trim()) ? [{ label: 'Plataforma', value: achValues.platform || achValues.platformCustom.trim() }] : []),
          { label: 'Nombre', value: achValues.firstName },
          { label: 'Apellido', value: achValues.lastName },
          { label: 'Banco', value: achValues.bankName },
          { label: 'Routing Number', value: achValues.routingNumber },
          { label: 'Número de cuenta', value: maskAccount(achValues.accountNumber), testId: 'confirm-account-masked' },
          { label: 'Tipo de cuenta', value: achValues.accountType === 'checking' ? 'Checking' : 'Savings' },
          ...(achValues.street
            ? [{ label: 'Dirección', value: `${achValues.street}, ${achValues.city}, ${achValues.state} ${achValues.zip}, United States` }]
            : []),
          ...(achValues.screenshotFile ? [{ label: 'Captura', value: achValues.screenshotFile.name }] : []),
        ]
      : selectedModality === 'USDT'
      ? [
          { label: 'Modalidad', value: 'USDT' },
          { label: 'Red', value: 'Polygon (MATIC)' },
          ...((usdtValues.usdtPlatform || usdtValues.usdtPlatformCustom.trim()) ? [{ label: 'Plataforma / Wallet', value: usdtValues.usdtPlatform || usdtValues.usdtPlatformCustom.trim() }] : []),
          { label: 'Wallet', value: maskWalletAddress(usdtValues.walletAddress), testId: 'confirm-usdt-wallet' },
        ]
      : [
          { label: 'Modalidad', value: 'AR$ vía LB Finanzas' },
          { label: 'LB Tag', value: `$${arsValues.lbTag}`, testId: 'confirm-ars-lb-tag' },
          { label: 'CUIT', value: arsValues.cuit, testId: 'confirm-ars-cuit' },
        ];

  const handleConfirmSave = async () => {
    let data: PaymentData;
    if (selectedModality === 'USD') {
      data = {
        currency: 'USD',
        plataforma: achValues.platform || achValues.platformCustom.trim(),
        nombre: achValues.firstName,
        apellido: achValues.lastName,
        nombreBanco: achValues.bankName,
        routingNumber: achValues.routingNumber,
        numeroCuenta: achValues.accountNumber,
        tipoCuenta: achValues.accountType,
        direccionBanco: {
          calle: achValues.street,
          ciudad: achValues.city,
          estado: achValues.state,
          codigoPostal: achValues.zip,
          pais: 'United States',
        },
        // `achValues.screenshotFile` NO se manda — ver TODO en AchForm.
      };
    } else if (selectedModality === 'USDT') {
      data = {
        currency: 'USDT',
        red: FIXED_USDT_NETWORK,
        plataformaOWallet: usdtValues.usdtPlatform || usdtValues.usdtPlatformCustom.trim(),
        direccionWallet: usdtValues.walletAddress,
      };
    } else {
      data = {
        currency: 'ARS',
        lbTag: arsValues.lbTag,
        // psima valida con /^\d{2}-\d{8}-\d{1}$/ (con guiones) — mandar el
        // dato ya formateado, no cuitDigits() (dígitos pelados rompía el 400
        // de psima siempre, ver route.ts del repo de psima).
        cuit: arsValues.cuit,
      };
    }

    setIsSaving(true);
    setSubmitError(null);
    try {
      const result = await savePaymentInfo(data);
      setShowConfirm(false);
      handleClose();
      onSaved({ data, status: result.status });
    } catch (error) {
      setShowConfirm(false);
      if (error instanceof PaymentInfoFieldError) {
        applyFieldErrors(error.errors);
      } else {
        setSubmitError('No pudimos guardar tus datos de cobro. Intentá de nuevo en unos minutos.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open && !showConfirm} onOpenChange={v => { if (!v) handleClose(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 pb-6 pt-4 flex flex-col gap-5">
            <div className="flex items-center justify-between">
              <DialogHeader>
                <DialogTitle>Datos de cobro</DialogTitle>
              </DialogHeader>
              <DialogClose asChild>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={handleClose}
                  aria-label="Cerrar"
                >
                  <X size={18} />
                </button>
              </DialogClose>
            </div>

            {/* Modification warning banner */}
            {isEditing && (
              <div
                data-testid="modification-warning-banner"
                className="flex gap-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3"
              >
                <AlertCircle size={15} className="text-yellow-600 shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800 leading-snug">
                  <strong>Los cambios aplican a partir de la próxima liquidación.</strong>{' '}
                  Realizá las modificaciones antes del último día del mes para que se apliquen en el período en curso.
                </p>
              </div>
            )}

            {/* Modality selector — accordion */}
            <div className="flex flex-col gap-2" data-testid="modality-selector">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Modalidad de pago
              </p>
              {(() => {
                const handleModalityClick = (m: Currency) => {
                  // Toggle: click on already-selected collapses the form
                  if (selectedModality === m) {
                    setSelectedModality(null);
                    return;
                  }
                  // Warn if switching away from a saved modality
                  if (isEditing && initialCurrency && initialCurrency !== m) {
                    setPendingModality(m);
                    return;
                  }
                  setSelectedModality(m);
                };
                const showAll = selectedModality === null;
                const CARDS: { modality: Currency; label: string; description: string; icon: React.ReactNode }[] = [
                  { modality: 'USD', label: 'USD vía ACH', description: 'Transferencia bancaria en dólares', icon: <DollarSign size={16} className={selectedModality === 'USD' ? 'text-primary' : 'text-gray-500'} /> },
                  { modality: 'USDT', label: 'USDT vía Polygon', description: 'Stablecoin en dólares digitales', icon: <Bitcoin size={16} className={selectedModality === 'USDT' ? 'text-primary' : 'text-gray-500'} /> },
                  { modality: 'ARS', label: 'AR$ vía LB Finanzas', description: 'Pesos argentinos vía LB Finanzas', icon: <Coins size={16} className={selectedModality === 'ARS' ? 'text-primary' : 'text-gray-500'} /> },
                ];
                return CARDS
                  .filter(c => showAll || c.modality === selectedModality)
                  .map(c => (
                    <ModalityCard
                      key={c.modality}
                      modality={c.modality}
                      label={c.label}
                      description={c.description}
                      icon={c.icon}
                      selected={selectedModality === c.modality}
                      onSelect={() => handleModalityClick(c.modality)}
                    />
                  ));
              })()}
            </div>

            {/* Warning dialog: switching modality when data already saved */}
            {pendingModality !== null && (
              <div
                data-testid="modality-change-warning"
                className="flex flex-col gap-3 rounded-xl border border-yellow-300 bg-yellow-50 p-4"
              >
                <div className="flex gap-2">
                  <AlertCircle size={16} className="text-yellow-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-yellow-800 leading-snug">
                    <strong>¿Querés cambiar la modalidad de cobro?</strong>{' '}
                    Tus datos actuales ({initialCurrency === 'USD' ? 'USD vía ACH' : initialCurrency === 'USDT' ? 'USDT vía Polygon' : 'AR$ vía LB Finanzas'}) serán reemplazados.
                    Los datos anteriores quedarán guardados como historial.
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPendingModality(null)}
                    data-testid="modality-change-cancel-btn"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => { setSelectedModality(pendingModality); setPendingModality(null); }}
                    data-testid="modality-change-confirm-btn"
                  >
                    Sí, cambiar modalidad
                  </Button>
                </div>
              </div>
            )}

            {/* USD/ACH form */}
            {selectedModality === 'USD' && (
              <div className="flex flex-col gap-5" data-testid="ach-form">
                <div className="h-px bg-gray-100" />
                <AchForm
                  values={achValues}
                  errors={achErrors}
                  onChange={handleAchChange}
                  onBlur={handleAchBlur}
                  isCustomMode={isCustomPlatformMode}
                  onCustomModeChange={setIsCustomPlatformMode}
                />
                <SubmitErrorBanner error={submitError} />
                <Button
                  variant="default"
                  className="w-full mt-1"
                  onClick={handleAchSubmit}
                  data-testid="ach-submit-btn"
                >
                  Revisar y guardar
                </Button>
              </div>
            )}

            {/* ARS form */}
            {selectedModality === 'ARS' && (
              <div className="flex flex-col gap-5">
                <div className="h-px bg-gray-100" />
                <ArsForm
                  values={arsValues}
                  errors={arsErrors}
                  onChange={handleArsChange}
                  onBlur={handleArsBlur}
                />
                <SubmitErrorBanner error={submitError} />
                <Button
                  variant="default"
                  className="w-full mt-1"
                  onClick={handleArsSubmit}
                  data-testid="ars-submit-btn"
                >
                  Revisar y guardar
                </Button>
              </div>
            )}

            {/* USDT form */}
            {selectedModality === 'USDT' && (
              <div className="flex flex-col gap-5" data-testid="usdt-form-wrapper">
                <div className="h-px bg-gray-100" />
                <UsdtForm
                  values={usdtValues}
                  errors={usdtErrors}
                  onChange={handleUsdtChange}
                  onBlur={handleUsdtBlur}
                  isCustomWalletMode={isCustomWalletMode}
                  onCustomWalletModeChange={setIsCustomWalletMode}
                />
                <SubmitErrorBanner error={submitError} />
                <Button
                  variant="default"
                  className="w-full mt-1"
                  onClick={handleUsdtSubmit}
                  data-testid="usdt-submit-btn"
                >
                  Revisar y guardar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {showConfirm && (
        <ConfirmModal
          open={showConfirm}
          rows={confirmRows}
          saving={isSaving}
          onConfirm={handleConfirmSave}
          onBack={() => setShowConfirm(false)}
        />
      )}
    </>
  );
};
